import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  User,
  Package,
  CheckCircle,
  Loader2,
  AlertCircle
} from "lucide-react";

const conditionLabels = {
  nuevo: { label: "Nuevo", color: "bg-green-100 text-green-700" },
  bueno: { label: "Buen estado", color: "bg-blue-100 text-blue-700" },
  aceptable: { label: "Aceptable", color: "bg-yellow-100 text-yellow-700" }
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserving, setReserving] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      toast.error("Error al cargar el producto");
      navigate("/catalogo");
    } finally {
      setLoading(false);
    }
  };

  const handleReserve = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para reservar");
      navigate("/login", { state: { from: { pathname: `/producto/${productId}` } } });
      return;
    }

    if (user.points < (product?.points_cost || 50)) {
      toast.error("No tienes suficientes puntos para esta reserva");
      return;
    }

    setReserving(true);
    try {
      const response = await axios.post(
        `${API}/products/${productId}/reserve`,
        {},
        { withCredentials: true }
      );
      toast.success("¡Producto reservado! Tienes 7 días para recogerlo");
      navigate("/mis-reservas");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al reservar";
      toast.error(message);
    } finally {
      setReserving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-600">Producto no encontrado</h3>
          </div>
        </div>
      </Layout>
    );
  }

  const isOwner = user?.user_id === product.owner_id;
  const isAvailable = product.status === "listed";
  const canReserve = user && isAvailable && !isOwner && user.points >= product.points_cost;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/catalogo" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#374B67] mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Volver al catálogo</span>
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image */}
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden bg-slate-100">
              <img
                src={product.image_url || "https://via.placeholder.com/600x600?text=Sin+imagen"}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {/* Condition Badge */}
            <Badge className={`absolute top-4 left-4 ${conditionLabels[product.condition]?.color || "bg-slate-100"}`}>
              {conditionLabels[product.condition]?.label || product.condition}
            </Badge>
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-[#374B67] mb-2" data-testid="product-name">
                {product.name}
              </h1>
              <div className="flex items-center gap-4 text-slate-500">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>Por {product.owner_name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>
                    {new Date(product.created_at).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                      year: "numeric"
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Points Card */}
            <Card className="border-[#69C7EA] bg-gradient-to-r from-[#69C7EA]/5 to-transparent">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Costo de reserva</p>
                  <p className="text-3xl font-bold text-[#374B67]">{product.points_cost} <span className="text-lg">Puntos</span></p>
                </div>
                {user && (
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Tus puntos</p>
                    <p className={`text-2xl font-bold ${user.points >= product.points_cost ? "text-green-600" : "text-red-500"}`}>
                      {user.points}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Description */}
            <div>
              <h2 className="text-lg font-semibold text-[#374B67] mb-2">Descripción</h2>
              <p className="text-slate-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Location */}
            <Card className="bg-slate-50 border-0">
              <CardContent className="p-4 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#374B67] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#374B67]">Punto Limpio de Majadahonda</p>
                  <p className="text-sm text-slate-500">Carretera de El Plantío, 47, 28221</p>
                  <p className="text-sm text-slate-500 mt-1">L-V: 15:00-20:00 | Sáb: 08:00-20:00 | Dom: 09:00-14:00</p>
                </div>
              </CardContent>
            </Card>

            {/* Status & Actions */}
            {product.status === "reserved" && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <p className="text-yellow-700">Este producto ya está reservado</p>
              </div>
            )}

            {product.status === "delivered" && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-700">Este producto ya ha sido entregado</p>
              </div>
            )}

            {isAvailable && (
              <div className="space-y-3">
                {!user ? (
                  <Link to="/login" state={{ from: { pathname: `/producto/${productId}` } }}>
                    <Button className="w-full h-12 bg-[#374B67] hover:bg-[#2C3E56] text-base rounded-full" data-testid="login-to-reserve-btn">
                      Inicia sesión para reservar
                    </Button>
                  </Link>
                ) : isOwner ? (
                  <div className="bg-slate-100 rounded-xl p-4 text-center text-slate-600">
                    Este es tu producto
                  </div>
                ) : user.points < product.points_cost ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center text-red-600">
                    No tienes suficientes puntos. Necesitas {product.points_cost} puntos.
                  </div>
                ) : (
                  <Button
                    onClick={handleReserve}
                    disabled={reserving}
                    className="w-full h-12 bg-[#374B67] hover:bg-[#2C3E56] text-base rounded-full"
                    data-testid="reserve-btn"
                  >
                    {reserving ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Reservando...
                      </>
                    ) : (
                      "¡Me interesa! Reservar"
                    )}
                  </Button>
                )}
                
                <p className="text-sm text-slate-500 text-center">
                  Tendrás 7 días para recoger el producto en el Punto Limpio
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;
